package com.neurobs.js.dom

import org.scalajs.dom.{html, raw}
import com.neurobs.js.view
import scalatags.JsDom.all._


case class InputCheckBox(val input: html.Input) extends view.CheckBox {
	override def checked: Boolean = input.checked
	override def setChecked(checked: Boolean): Unit = {
		input.checked = checked
	}
	override def setChangeHandler(handler: () => Unit): Unit = {
		input.onchange = (_:raw.Event) => handler()
	}
	override def setReadOnly(ro: Boolean): Unit = {
		input.disabled = ro
	}
}

case class InputRadio(val input: html.Input) extends view.Radio {
	override def checked: Boolean = input.checked
	override def setChecked(checked: Boolean): Unit = { input.checked = checked }
	override def setSelectHandler(handler: () => Unit): Unit = { input.onchange = (_:raw.Event) => handler() }
	override def setReadOnly(ro: Boolean): Unit = { input.readOnly = ro }
	override def name: String = input.name
	override def setName(name: String): Unit = { input.name = name }
}

case class InputTextBox(val text: html.Input) extends view.TextBox {
	override def exactValue: String = text.value
	override def setValue(value: String): Unit = { text.value = value }
	override def setReadOnly(ro: Boolean): Unit = { text.readOnly = ro }
	override def setDisabled(d: Boolean): Unit = { text.disabled = d }
}

case class TextAreaTextBox(val area: html.TextArea) extends view.TextBox {
	override def exactValue: String = area.value
	override def setValue(value: String): Unit = { area.value = value }
	override def setReadOnly(ro: Boolean): Unit = { area.readOnly = ro }
	override def setDisabled(d: Boolean): Unit = { area.disabled = d }
}

class DivTextBox extends view.TextBox {
	val element = div(style := "background: Gainsboro; padding: 0.5em; min-width: 10em; white-space: pre-wrap;", contenteditable := "true").render

	override protected def exactValue: String = element.innerHTML
	override def setValue(value: String): Unit = { element.innerHTML = value }
	override def setReadOnly(ro: Boolean): Unit = setDisabled(ro)
	override def setDisabled(d: Boolean): Unit = {
		element.contentEditable = if (d) "false" else "true"
		element.style.background = if (d) "DimGrey" else "Gainsboro"
		element.style.color = if (d) "white" else "black"
	}
}

case class ButtonButton(button: html.Button) extends view.Button {
	override def setEnabled(enabled: Boolean): Unit = { button.disabled = !enabled }
	override def setHandler(handler: () => Unit): Unit = { button.onclick = (_:raw.Event) => handler() }
	override def setVisibility(visible: Boolean): Unit = { button.style.visibility = if (visible) "visible" else "hidden"}
	override def setDisplay(display: Boolean): Unit = { button.style.display = if (display) "inline-block" else "none" }
	override def setCaption(c: String): Unit = { button.innerHTML = c }
}

case class ElementText(element: raw.HTMLElement) extends view.Text {
	override def value: String = element.textContent
	override def setValue(value: String): Unit = { element.innerHTML = DOMViewElement.escapeForHTML(value) }
}

case class AnchorLink(anchor: html.Anchor) extends view.Link {
	override def setHandler(handler: () => Unit): view.Link = { anchor.onclick = (_:raw.MouseEvent) => handler(); this }
	override def setActive(active: Boolean): Unit = Unit
	override def setHref(url: String): Unit = {
		anchor.href = url
		anchor.target = "_blank"
	}
	override def setText(t: String): Unit = { anchor.innerHTML = DOMViewElement.escapeForHTML(t) }
	override def getText(): String = anchor.text
}

case class SelectSelectBox(val select: html.Select) extends view.SelectBox {
	override def value: Option[String] = if (select.selectedIndex == -1) None else Some(select.value)
	override def selectedIndex: Option[Int] = if (select.selectedIndex == -1) None else Some(select.selectedIndex)
	override def setSelected(i: Option[Int]): Unit = { select.selectedIndex = i.getOrElse(-1) }
	override def setOptions(options: Seq[String], selection: Option[Int] = None): Unit = {
		select.innerHTML = ""
		for (i <- options) select.appendChild(option(i).render)
		select.selectedIndex = selection.getOrElse(-1)
	}
	override def setSize(size: Int): Unit = { select.size = size }
	override def setChangeHandler(handler: () => Unit): Unit = {
		select.onchange = (_:raw.Event) => handler()
	}
}

case class ForceSelectBox(val select: html.Select) extends view.SelectBox {
	override def value: Option[String] = if (select.selectedIndex < 1) None else Some(select.value)
	override def selectedIndex: Option[Int] = if (select.selectedIndex < 1) None else Some(select.selectedIndex - 1)
	override def setSelected(i: Option[Int]): Unit = { select.selectedIndex = i.map(_ + 1).getOrElse(0) }
	override def setOptions(options: Seq[String], selection: Option[Int] = None): Unit = {
		select.innerHTML = ""
		select.appendChild(option("Please Select...", disabled).render)
		for (i <- options) select.appendChild(option(i).render)
		select.selectedIndex = selection.map(_ + 1).getOrElse(0)
	}
	override def setSize(size: Int): Unit = { select.size = size }
	override def setChangeHandler(handler: () => Unit): Unit = {
		select.onchange = (_:raw.Event) => handler()
	}
	setOptions(List())
}

case class ElementContainer(val element: raw.HTMLElement) extends view.Container {
	override def clear(): Unit = {
		element.innerHTML = ""
	}
	override def addChild(child: view.Container): Unit = child match {
		case ElementContainer(c) => element.appendChild(c)
		case _ =>
	}
	override def removeChild(child: view.Container): Unit = child match {
		case ElementContainer(c) => element.removeChild(c)
		case _ =>
	}
}

object DOMViewElement {
	implicit val inputCheckBox = InputCheckBox.apply(_)
	implicit val inputTextBox = InputTextBox.apply(_)
	implicit val textAreaTextBox = TextAreaTextBox.apply(_)
	implicit val buttonButton = ButtonButton.apply(_)
	implicit val elementText = ElementText.apply(_)
	implicit val anchorLink = AnchorLink.apply(_)
	implicit val selectSelectBox = SelectSelectBox.apply(_)
	implicit val elementContainer = ElementContainer.apply(_)


	def escapeForHTML(s: String): String = {
		div(s).render.innerHTML.replace("\n", "<br>")
	}
}